import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { Table, TableBody, TableHeader, TableHeaderColumn, TableRow } from 'material-ui/Table';
import Pagination from 'components/Libs/Pagination';
import Dialog from 'material-ui/Dialog';

import { getVessels } from 'actions/vessels/vessels';
import api from 'services';
import styles from 'styles';
import VesselRow from './Row';
import VesselImport from './VesselImport';

import VesselsListFilters from './Filters';
import { CenterBox } from './utils';

function VesselListTable(props) {
    const { vessels, getVessels } = props;

    const rowsPerPage = [25, 50, 100];

    const [rows, setRows] = useState([]);
    const [numberOfRows, setNumberOfRows] = useState(25);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(undefined);
    const [notConfirmed, setNotConfirmed] = useState(false);
    const [sanctioned, setSanctioned] = useState(undefined);
    const [vesselImportModalOpen, setVesselImportModalOpen] = useState(false);
    const [isNameSuggestion, setIsNameSuggestion] = useState(false);
    const [modifiedFilter, setModifiedFilter] = useState('all');
    const [photosFilter, setPhotosFilter] = useState('all');
    const [filterCargoExclusions, setFilterCargoExclusions] = useState(null);
    const [filterTradingRestrictions, setFilterTradingRestrictions] = useState(null);
    const [search] = useState();
    const [typeByPurpose, setTypeByPurpose] = useState('');
    const [typeBySpecialty, setTypeBySpecialty] = useState('');
    const [dwtMin, setDwtMin] = useState(null);
    const [dwtMax, setDwtMax] = useState(null);
    const [bltMin, setBltMin] = useState(null);
    const [bltMax, setBltMax] = useState(null);
    const [sortField, setSortField] = useState('name');
    const [sortValue, setSortValue] = useState(1);

    const searchRef = useRef(null);
    const importDialogRef = useRef(null);

    useEffect(() => {
        if (!vessels) return;
        setTotal(vessels.total);
        setRows(vessels.data);
        setPage(vessels.page);
        setNumberOfRows(vessels.limit);
        setNotConfirmed(vessels.notConfirmed);
        setSanctioned(vessels.sanctioned);
        setFilterCargoExclusions(vessels.filterCargoExclusions);
        setFilterTradingRestrictions(vessels.filterTradingRestrictions);
        setIsNameSuggestion(vessels.isNameSuggestion);
        setPhotosFilter(vessels.photosFilter);
        setTypeByPurpose(vessels.typeByPurpose || '');
        setTypeBySpecialty(vessels.typeBySpecialty || '');
        setDwtMin(vessels.dwtMin || null);
        setDwtMax(vessels.dwtMax || null);
        setBltMin(vessels.bltMin || null);
        setBltMax(vessels.bltMax || null);
        setSortField(vessels.sortField);
        setSortValue(typeof vessels.sortValue === 'number' ? vessels.sortValue : 1);
    }, [vessels]);

    const clearSearch = useCallback((_e, value) => {
        if ((value || '').length === 0) {
            getVessels({
                page,
                limit: numberOfRows,
            });
        }
    }, [getVessels, page, numberOfRows]);

    const handleSearch = useCallback(async () => {
        const value = ((searchRef.current && searchRef.current.input && searchRef.current.input.value) || '').trim();
        if (searchRef.current && searchRef.current.input) {
            searchRef.current.input.value = value;
        }
        if (!value.length) {
            return;
        }
        await api.findVessel(value, !notConfirmed, isNameSuggestion)
            .then((response) => {
                if (response.status === 200) {
                    setRows(response.data.data);
                    setPage(1);
                    setNumberOfRows(25);
                }
            });
    }, [notConfirmed, isNameSuggestion]);

    const updateRows = useCallback((state) => {
        getVessels({
            page: state.page,
            limit: state.numberOfRows,
            notConfirmed,
            sanctioned,
            isNameSuggestion,
            photosFilter,
            typeByPurpose,
            dwtMin,
            dwtMax,
            typeBySpecialty,
            bltMin,
            bltMax,
            sortField,
            sortValue,
        });
    }, [
        getVessels, 
        notConfirmed, 
        sanctioned, 
        isNameSuggestion, 
        photosFilter, 
        typeByPurpose, 
        dwtMin, 
        dwtMax, 
        sortField, 
        sortValue,
        typeBySpecialty,
        bltMin,
        bltMax,
    ]);

    const handleCloseImportVessels = useCallback(() => setVesselImportModalOpen(false), []);
    const handleUpdateImportDialog = useCallback(() => {
        if (importDialogRef.current && typeof importDialogRef.current.forceUpdate === 'function') {
            importDialogRef.current.forceUpdate();
        }
    }, []);

    const handleFilters = useCallback((fieldOrUpdates, value) => {
        const updates = typeof fieldOrUpdates === 'object' ? fieldOrUpdates : { [fieldOrUpdates]: value };

        if ('typeByPurpose' in updates) {
            setTypeByPurpose(updates.typeByPurpose || '');

            if (!updates.typeByPurpose) {
                updates.typeBySpecialty = '';
            }
        }
        
        if ('typeBySpecialty' in updates) setTypeBySpecialty(updates.typeBySpecialty);
    
        if ('isNameSuggestion' in updates) setIsNameSuggestion(!!updates.isNameSuggestion);
        if ('notConfirmed' in updates) setNotConfirmed(!!updates.notConfirmed);
        if ('sanctioned' in updates) setSanctioned(updates.sanctioned);
        if ('modifiedFilter' in updates) setModifiedFilter(updates.modifiedFilter);
        if ('photosFilter' in updates) setPhotosFilter(updates.photosFilter);
        if ('dwtMin' in updates) setDwtMin(updates.dwtMin || null);
        if ('dwtMax' in updates) setDwtMax(updates.dwtMax || null);
        if ('bltMin' in updates) setBltMin(updates.bltMin || null);
        if ('bltMax' in updates) setBltMax(updates.bltMax || null);
        if ('sortField' in updates) setSortField(updates.sortField || 'name');
        if ('sortValue' in updates) setSortValue(updates.sortValue);
    
        const next = {
            isNameSuggestion,
            notConfirmed,
            sanctioned,
            modifiedFilter,
            photosFilter,
            typeByPurpose,
            dwtMin,
            dwtMax,
            typeBySpecialty,
            bltMin,
            bltMax,
            sortField,
            sortValue,
            ...updates,
        };

        if (next.bltMin && String(next.bltMin).length !== 4) return;
        if (next.bltMax && String(next.bltMax).length !== 4) return;
        if (next.bltMin && next.bltMax && next.bltMin > next.bltMax) return;
    
        getVessels({
            page: 1,
            limit: numberOfRows,
            isNameSuggestion: !!next.isNameSuggestion,
            notConfirmed: !!next.notConfirmed,
            sanctioned: next.sanctioned,
            photosFilter: next.photosFilter,
            typeByPurpose: next.typeByPurpose,
            dwtMin: next.dwtMin,
            dwtMax: next.dwtMax,
            typeBySpecialty: next.typeBySpecialty,
            bltMin: next.bltMin,
            bltMax: next.bltMax,
            sortField: next.sortField,
            sortValue: next.sortValue,
        });
    }, [
        getVessels, 
        numberOfRows, 
        isNameSuggestion, 
        notConfirmed, 
        sanctioned, 
        photosFilter, 
        typeByPurpose, 
        dwtMin, 
        dwtMax, 
        sortField, 
        sortValue,
        typeBySpecialty,
        bltMin,
        bltMax,
    ]);

    const handleNameSuggested = useCallback(() => {
        handleFilters('isNameSuggestion', !isNameSuggestion);
    }, [isNameSuggestion]);

    const handleConfirmed = useCallback(() => {
        handleFilters('notConfirmed', !notConfirmed);
    }, [notConfirmed]);

    const handleSanctioned = useCallback(() => {
        const next = (!sanctioned) || undefined;
        handleFilters('sanctioned', next);
    }, [sanctioned]);
    
    const handleExclusions = useCallback((period, property) => {
        if (!property) return;
        const nextVal = period ? new Date(period).toISOString() : undefined;

        if (property === 'filterCargoExclusions') {
            setFilterCargoExclusions(nextVal);
        } else if (property === 'filterTradingRestrictions') {
            setFilterTradingRestrictions(nextVal);
        }

        getVessels({
            page,
            limit: numberOfRows,
            isNameSuggestion,
            notConfirmed,
            sanctioned,
            photosFilter,
            [property]: nextVal,
        });
    }, [getVessels, page, numberOfRows, isNameSuggestion, notConfirmed, sanctioned, photosFilter]);

    const pagination = (
        <div
            className="center-xs middle-xs"
            style={{ display: 'flex' }}
        >
            <Pagination
                total={total}
                rowsPerPage={rowsPerPage}
                page={page}
                numberOfRows={numberOfRows}
                updateRows={(s) => {
                    setPage(s.page);
                    setNumberOfRows(s.numberOfRows);
                    updateRows(s);
                }}
            />
        </div>
    );

    return (
        <div>
            <Dialog
                ref={importDialogRef}
                title="Import/Update vessels from LOTUS"
                modal={false}
                open={vesselImportModalOpen}
                onRequestClose={handleCloseImportVessels}
                autoScrollBodyContent
                style={{ height: '60vh', width: '90vw' }}
            >
                <VesselImport onUpdate={handleUpdateImportDialog} />
            </Dialog>
    
            <VesselsListFilters
                searchRef={searchRef}
                defaultSearchValue={search}
                clearSearch={clearSearch}
                handleSearch={handleSearch}
                isNameSuggestion={isNameSuggestion}
                notConfirmed={notConfirmed}
                handleNameSuggested={handleNameSuggested}
                handleConfirmed={handleConfirmed}
                modifiedFilter={modifiedFilter}
                handleFilters={handleFilters}
                filterCargoExclusions={filterCargoExclusions}
                filterTradingRestrictions={filterTradingRestrictions}
                handleExclusions={handleExclusions}
                photosFilter={photosFilter}
                sanctioned={sanctioned}
                handleSanctioned={handleSanctioned}
                typeByPurpose={typeByPurpose}
                dwtMin={dwtMin}
                dwtMax={dwtMax}
                sortValue={sortValue}
                sortField={sortField}
                typeBySpecialty={typeBySpecialty}
                bltMin={bltMin}
                bltMax={bltMax}
                rightPart={
                    <React.Fragment>
                        <div className="center-xs middle-xs" style={{ display: 'flex' }}>
                            <span>VESSELS: {vessels.total}</span>
                        </div>
                        <CenterBox>{pagination}</CenterBox>
                    </React.Fragment>
                }
            />
    
            <div className="table-body">
                <Table selectable={false}>
                    <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                        <TableRow>
                            <TableHeaderColumn style={styles.tableRowColumnSmallCSS}>imoNumber</TableHeaderColumn>
                            <TableHeaderColumn style={styles.tableRowColumnSmallCSS}>name</TableHeaderColumn>
                            <TableHeaderColumn style={styles.tableRowColumnSmallCSS}>Year</TableHeaderColumn>
                            <TableHeaderColumn style={styles.tableHeaderColumnCSS}>Type By Purpose</TableHeaderColumn>
                            <TableHeaderColumn style={styles.tableHeaderColumnCSS}>Class</TableHeaderColumn>
                            <TableHeaderColumn style={styles.tableHeaderColumnCSS}>Gears</TableHeaderColumn>
                            <TableHeaderColumn style={styles.tableRowColumnSmallCSS}>Capacity (G/B)</TableHeaderColumn>
                            <TableHeaderColumn style={styles.tableRowColumnSmallCSS}>DWT</TableHeaderColumn>
                            <TableHeaderColumn style={styles.tableRowColumnModified}>Modified</TableHeaderColumn>
                            <TableHeaderColumn style={styles.tableRowColumnModified}>Sanction (RT)</TableHeaderColumn>
                            <TableHeaderColumn style={styles.tableRowColumnSmallCSS}>Actions</TableHeaderColumn>
                        </TableRow>
                    </TableHeader>
    
                    <TableBody displayRowCheckbox={false}>
                        {rows.map(item => (
                            <VesselRow key={item._id} item={item} />
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

export default connect(
    ({ vessels, user }) => ({
        vessels,
        userId: user._id,
    }),
    dispatch => bindActionCreators(
        {
            getVessels,
        },
        dispatch,
    ),
)(VesselListTable);



// WEBPACK FOOTER //
// ./app/components/Vessels/List/Table.jsx